import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';

export const COLOR_MODES = {
  day: 'day',
  night: 'night',
};

export const DEFAULT_COLOR_MODE = COLOR_MODES.day;
export const COLOR_MODE_STORAGE_KEY = 'auralux-color-mode';

const ColorModeContext = createContext(null);

function readStoredColorMode() {
  try {
    const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    if (stored === COLOR_MODES.day || stored === COLOR_MODES.night) return stored;
  } catch {
    /* ignore */
  }
  return DEFAULT_COLOR_MODE;
}

export function ColorModeProvider({ children }) {
  const [colorMode, setColorModeState] = useState(readStoredColorMode);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-color-mode', colorMode);
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, colorMode);
    } catch {
      /* ignore */
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute('content', colorMode === COLOR_MODES.night ? '#0A0F1A' : '#F5F6F8');
    }
  }, [colorMode]);

  const setColorMode = useCallback((mode) => {
    if (mode === COLOR_MODES.day || mode === COLOR_MODES.night) {
      setColorModeState(mode);
    }
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorModeState((prev) => (prev === COLOR_MODES.day ? COLOR_MODES.night : COLOR_MODES.day));
  }, []);

  const value = useMemo(
    () => ({
      colorMode,
      isDay: colorMode === COLOR_MODES.day,
      isNight: colorMode === COLOR_MODES.night,
      setColorMode,
      toggleColorMode,
    }),
    [colorMode, setColorMode, toggleColorMode],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export function useColorMode() {
  const ctx = useContext(ColorModeContext);
  if (!ctx) throw new Error('useColorMode must be used within ColorModeProvider');
  return ctx;
}
